import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import RegistrationForm from '../../components/RegistrationForm';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const RegistrationPage = () => {
  const { sportId } = useParams();
  const navigate = useNavigate();
  const [sport, setSport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [registrationId, setRegistrationId] = useState(null);



  useEffect(() => {
    // Fetch sport details from API
    const fetchSport = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/sports/${sportId}`);
        
        if (!response.ok) {
          throw new Error('Sport not found');
        }
        
        const data = await response.json();
        setSport(data.sport);
      } catch (err) {
        console.error('Error fetching sport:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (sportId) {
      fetchSport();
    }
  }, [sportId]);

  const handleRegistrationSubmit = async (registrationData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/registrations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...registrationData,
          sportId: parseInt(sportId),
          academicYear: new Date().getFullYear().toString(),
          season: sport?.season || 'Fall'
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Registration submission failed');
      }
      
      const result = await response.json();
      
      setRegistrationId(result.data.id);
      setSubmissionStatus('success');
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error('Registration submission error:', error);
      setSubmissionStatus('error');
      throw error; // Re-throw to let form handle the error
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-gray-600">Loading registration form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Sport Not Found</h1>
          <p className="text-gray-600 mb-6">
            The sport you're looking for doesn't exist or registration is not available.
          </p>
          <button
            onClick={() => navigate('/sports')}
            className="btn-primary"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Sports
          </button>
        </div>
      </div>
    );
  }

  if (submissionStatus === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Registration Submitted Successfully!
            </h1>
            <p className="text-gray-600 mb-6">
              Your {sport.name} registration has been submitted and is being reviewed.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <InformationCircleIcon className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-left">
                  <h3 className="font-medium text-blue-900 mb-2">What happens next?</h3>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Your registration will be reviewed within 2-3 business days</li>
                    <li>• You'll receive an email confirmation at the address provided</li>
                    <li>• Any missing documents will be requested via email</li>
                    <li>• Once approved, you'll receive practice and payment information</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-gray-900 mb-2">Registration Details</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Registration ID:</strong> {registrationId}</p>
                <p><strong>Sport:</strong> {sport.name}</p>
                <p><strong>Status:</strong> <span className="text-yellow-600">Pending Review</span></p>
                <p><strong>Submitted:</strong> {new Date().toLocaleDateString()}</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/sports')}
                className="btn-primary w-full"
              >
                Back to Sports
              </button>
              <button
                onClick={() => window.print()}
                className="btn-secondary w-full"
              >
                Print Confirmation
              </button>
            </div>
            
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Questions? Contact {sport.contact.coach} at{' '}
                <a href={`mailto:${sport.contact.email}`} className="text-primary hover:text-primary-dark">
                  {sport.contact.email}
                </a>{' '}
                or{' '}
                <a href={`tel:${sport.contact.phone}`} className="text-primary hover:text-primary-dark">
                  {sport.contact.phone}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/sports')}
            className="flex items-center text-primary hover:text-primary-dark mb-4"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Sports
          </button>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {sport.name} Registration
                </h1>
                <p className="text-gray-600 mb-4">{sport.description}</p>
                <div className="flex items-center space-x-6 text-sm text-gray-500">
                  <span><strong>Season:</strong> {sport.season}</span>
                  <span><strong>Coach:</strong> {sport.contact.coach}</span>
                  <span><strong>Registration Fee:</strong> ${sport.fees.registration}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Registration Form */}
          <div className="lg:col-span-2">
            <RegistrationForm 
              sport={sport} 
              onSubmit={handleRegistrationSubmit}
            />
          </div>

          {/* Sidebar Information */}
          <div className="space-y-6">
            {/* Important Dates */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Important Dates
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Registration Deadline:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(sport.deadlines.registration).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Physical Due:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(sport.deadlines.physicals).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Practices Start:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(sport.practices.start).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Fee Breakdown */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Fee Breakdown
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Registration Fee:</span>
                  <span className="font-medium text-gray-900">${sport.fees.registration}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Uniform:</span>
                  <span className="font-medium text-gray-900">${sport.fees.uniform}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Equipment:</span>
                  <span className="font-medium text-gray-900">${sport.fees.equipment}</span>
                </div>
                <div className="border-t border-gray-200 pt-3 flex justify-between">
                  <span className="font-medium text-gray-900">Total:</span>
                  <span className="font-bold text-gray-900">
                    ${sport.fees.registration + sport.fees.uniform + sport.fees.equipment}
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Payment plans available. Contact the office for details.
              </p>
            </div>

            {/* Requirements */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Requirements
              </h3>
              <ul className="space-y-2 text-sm text-gray-600">
                {sport.requirements.map((requirement, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    {requirement}
                  </li>
                ))}
              </ul>
            </div>

            {/* Practice Schedule */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Practice Schedule
              </h3>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Start Date:</strong> {new Date(sport.practices.start).toLocaleDateString()}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Schedule:</strong> {sport.practices.schedule}
              </p>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Contact Information
              </h3>
              <div className="space-y-2 text-sm">
                <p>
                  <strong className="text-gray-900">{sport.contact.coach}</strong>
                </p>
                <p>
                  <a 
                    href={`mailto:${sport.contact.email}`}
                    className="text-primary hover:text-primary-dark"
                  >
                    {sport.contact.email}
                  </a>
                </p>
                <p>
                  <a 
                    href={`tel:${sport.contact.phone}`}
                    className="text-primary hover:text-primary-dark"
                  >
                    {sport.contact.phone}
                  </a>
                </p>
              </div>
            </div>

            {/* Help */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <InformationCircleIcon className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-blue-900 mb-1">Need Help?</h4>
                  <p className="text-sm text-blue-800">
                    If you have questions about the registration process, contact the athletic office at{' '}
                    <a href="tel:(555)123-4500" className="font-medium">(555) 123-4500</a> or{' '}
                    <a href="mailto:athletics@eastendprep.edu" className="font-medium">athletics@eastendprep.edu</a>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPage;